import { useEffect, useState } from "react";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, money } from "@/lib/api";
import { StaffLayout } from "@/components/StaffLayout";
import { Button } from "@/components/ui/button";

const COLORS = ["#D4AF37", "#4FA8D8", "#8B7BD8", "#4FD1A5"];

const EXPORTS = [
  { kind: "cases", label: "Case list" },
  { kind: "timeline", label: "Timelines" },
  { kind: "financial", label: "Financial" },
  { kind: "analytics", label: "Analytics" },
];

export default function StaffReports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/summary").then((res) => setData(res.data)).catch(() => toast.error("Unable to load reports"));
  }, []);

  const grab = (kind, format) =>
    downloadFile("/reports/export", `vineford-${kind}.${format}`, { kind, format }).catch(() => toast.error("Export failed"));

  return (
    <StaffLayout>
      <div>
        <p className="text-xs uppercase tracking-[0.28em] text-gold">Reporting</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl text-[#F3F4F6]">Portfolio analytics</h1>
      </div>

      {data ? (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5" data-testid="report-totals">
            {[
              ["Total cases", data.totals.cases],
              ["Active", data.totals.active],
              ["Closed", data.totals.closed],
              ["Avg progress", `${data.totals.avg_progress}%`],
              ["Invoiced", money(data.financial.total)],
            ].map(([k, v]) => (
              <div key={k} className="glass rounded-2xl p-6">
                <p className="font-display text-3xl text-gold">{v}</p>
                <p className="mt-2 text-[11px] uppercase tracking-widest text-[#9CA3AF]">{k}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="glass rounded-2xl p-7 lg:col-span-2">
              <h2 className="font-display text-xl text-[#F3F4F6]">Case volume by month</h2>
              <div className="mt-6 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.volume}>
                    <XAxis dataKey="month" stroke="#6B7280" fontSize={11} />
                    <YAxis stroke="#6B7280" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0A192F", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, color: "#F3F4F6" }} />
                    <Bar dataKey="count" fill="#D4AF37" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="glass rounded-2xl p-7">
              <h2 className="font-display text-xl text-[#F3F4F6]">Status breakdown</h2>
              <div className="mt-4 h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.status_breakdown} dataKey="count" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={3}>
                      {data.status_breakdown.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0A192F", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, color: "#F3F4F6" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-2">
                {data.status_breakdown.map((s, i) => (
                  <div key={s.status} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-[#D1D5DB]">
                      <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /> {s.label}
                    </span>
                    <span className="text-[#9CA3AF]">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="glass rounded-2xl p-7">
              <h2 className="font-display text-xl text-[#F3F4F6]">Investigator load</h2>
              <div className="mt-5 space-y-4" data-testid="investigator-load">
                {data.investigator_load.map((row) => (
                  <div key={row.investigator}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#D1D5DB]">{row.investigator}</span>
                      <span className="text-gold">{row.cases}</span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gold"
                        style={{ width: `${(row.cases / data.totals.cases) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass rounded-2xl p-7">
              <h2 className="font-display text-xl text-[#F3F4F6]">Financial position</h2>
              <div className="mt-5 grid grid-cols-2 gap-4">
                {[
                  ["Invoiced", data.financial.total],
                  ["Collected", data.financial.paid],
                  ["Outstanding", data.financial.unpaid],
                  ["Overdue", data.financial.overdue],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4">
                    <p className="font-display text-2xl text-[#F3F4F6]">{money(v)}</p>
                    <p className="mt-1 text-[11px] uppercase tracking-widest text-[#6B7280]">{k}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="glass rounded-2xl p-7">
            <h2 className="font-display text-xl text-[#F3F4F6]">Exports</h2>
            <p className="mt-2 text-sm text-[#9CA3AF]">Download any dataset as CSV or a branded PDF report.</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {EXPORTS.map((e) => (
                <div key={e.kind} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                  <p className="text-sm text-[#F3F4F6]">{e.label}</p>
                  <div className="mt-4 flex gap-2">
                    <Button
                      data-testid={`export-${e.kind}-csv`}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-white/20 bg-transparent hover:bg-white/10"
                      onClick={() => grab(e.kind, "csv")}
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5 text-gold" /> CSV
                    </Button>
                    <Button
                      data-testid={`export-${e.kind}-pdf`}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-white/20 bg-transparent hover:bg-white/10"
                      onClick={() => grab(e.kind, "pdf")}
                    >
                      <FileText className="h-3.5 w-3.5 mr-1.5 text-gold" /> PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <p className="text-sm text-[#9CA3AF]">Loading analytics…</p>
      )}
    </StaffLayout>
  );
}
